/** @jsxImportSource @emotion/react */

import { useTheme } from '@emotion/react';
import { forwardRef, Ref } from 'react';
import { SystemStyleObject } from '@styled-system/css';
import { Box } from '../Box/Box';
import { Heading } from '../Heading/Heading';
import { ModalBackdrop } from '../Modal/ModalBackdrop';
import { ModalContainer } from '../Modal/ModalContainer';
import { useRequiredPropWarning } from '../hooks/useRequiredPropWarning';
import { scrollStyles } from '../utils/useScrollStyles';
import { Stack } from '../Stack/Stack';
import { ModalCloseButton } from '../Modal/ModalCloseButton';
import { InfoModalProps } from './types';
import { InfoModalIcon } from './InfoModalIcon';

export { useDialogState as useInfoModalState } from 'reakit';
export { ModalDisclosure as InfoModalDisclosure } from '../Modal/ModalDisclosure';

export const InfoModal = forwardRef(
  (
    /* eslint-disable @typescript-eslint/no-unused-vars -- preventBodyScroll, hideOnEsc, hideOnClickOutside добавлены сюда, чтобы они не ломали поведение даже если проброшены */
    {
      kind,
      heading,
      styles,
      preventBodyScroll,
      hideOnEsc,
      hideOnClickOutside,
      ...props
    }: InfoModalProps,
    /* eslint-enable @typescript-eslint/no-unused-vars -- продолжаем проверять */
    ref: Ref<HTMLDivElement>
  ): JSX.Element => {
    const theme = useTheme();
    useRequiredPropWarning(Boolean(kind), 'InfoModal', 'kind');
    useRequiredPropWarning(Boolean(heading), 'InfoModal', 'heading');

    return (
      <ModalBackdrop
        {...props}
        py={['sp-32', 'sp-64']}
        px="sp-16"
        styles={{
          label: 'InfoModalBackdrop',
        }}
      >
        <ModalContainer
          {...props}
          ref={ref}
          maxWidth="infoModalMaxWidth"
          minHeight={0}
          maxHeight={1}
          styles={{
            label: 'InfoModalContainer',
            borderRadius: 'infomodal',
            alignSelf: 'center',
            ...styles,
          }}
        >
          {/* Обертка содержимого модалки */}
          <Box
            styles={
              {
                '@media (min-width: 48em)': scrollStyles(theme),
                label: 'ModalContent',
              } as SystemStyleObject
            }
            display="flex"
            flexDirection="column"
            flexGrow={1}
            position="relative"
            overflow="auto"
            maxHeight={1}
            minHeight={1}
            width={1}
          >
            {/* Блок заголовка */}
            <Stack
              space="sp-16"
              bg="white"
              pt={[
                'infoModalHeaderPaddingTopSmall',
                'infoModalHeaderPaddingTop',
              ]}
              pr={[
                'infoModalHeaderPaddingRightSmall',
                'infoModalHeaderPaddingRight',
              ]}
              pb={[
                'infoModalHeaderPaddingBottomSmall',
                'infoModalHeaderPaddingBottom',
              ]}
              pl={[
                'infoModalHeaderPaddingLeftSmall',
                'infoModalHeaderPaddingLeft',
              ]}
              styles={{ label: 'InfoModalHeader' }}
            >
              <InfoModalIcon kind={kind} />
              <Heading
                as="h2"
                level={[3, 2]}
                tabIndex={0}
                styles={{
                  outline: 0,
                }}
              >
                {heading}
              </Heading>
            </Stack>

            {/* Блок тела */}
            <Stack
              space={['sp-32', 'sp-40']}
              flexGrow={1}
              px={['sp-16', 'sp-40']}
              pt={['sp-16']}
              pb={['sp-16', 'sp-40']}
              styles={{ label: 'InfoModalBody' }}
            >
              {props.children}
            </Stack>
          </Box>
          <ModalCloseButton hide={props.hide} />
        </ModalContainer>
      </ModalBackdrop>
    );
  }
);
